import { Component, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { And, NumberEquals, RuleInterface, StringEquals } from '@vendasta/automata';
import { GalaxyInputModule, SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { Origin, Status } from '@vendasta/sales-orders';
import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { CommonTriggerPanelComponent } from '../../../common/common-trigger-panel.component';
import { TriggerBaseRules } from '../../../common/decorators/trigger-rules.decorator';
import { CommonModule } from '@angular/common';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { BrandingService } from '../../../../../data-services/whitelabel.service';

export const OrderStatusLabelMap = {
  [Status.RESUBMITTED]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.RESUBMITTED',
  [Status.SUBMITTED]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.SUBMITTED',
  [Status.SUBMITTED_FOR_CUSTOMER_APPROVAL]:
    'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.SUBMITTED_FOR_CUSTOMER_APPROVAL',
  [Status.APPROVED]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.APPROVED',
  [Status.PROCESSING]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.PROCESSING',
  [Status.ACTIVATION_ERRORS]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.ACTIVATION_ERRORS',
  [Status.ARCHIVED]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.ARCHIVED',
  [Status.FULFILLED]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.FULFILLED',
  [Status.DECLINED]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.DECLINED',
  [Status.SCHEDULED_ACTIVATION]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.SCHEDULED_ACTIVATION',
  [Status.DRAFTED]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.DRAFTED',
  [Status.CANCELLATION_REQUESTED]:
    'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.CANCELLATION_REQUESTED',
  [Status.CANCELLED]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.STATUS_OPTIONS.CANCELLED',
};

export const AnyOrigin = '*';
export const OrderOriginLabelMap = {
  [AnyOrigin]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ORIGIN_OPTIONS.ANY',
  [Origin[Origin.PC]]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ORIGIN_OPTIONS.PC',
  [Origin[Origin.SSC]]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ORIGIN_OPTIONS.SSC',
  [Origin[Origin.VBC]]: 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ORIGIN_OPTIONS.BUSINESS_APP',
};

@Component({
  selector: 'automata-order-status-changed-panel',
  styleUrls: ['./order-status-changed-panel.component.scss'],
  templateUrl: './order-status-changed-panel.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    GalaxyInputModule,
  ],
})
export class OrderStatusChangedPanelComponent extends CommonTriggerPanelComponent implements OnInit {
  orderStatusControl: UntypedFormControl = new UntypedFormControl(Status.SUBMITTED.toString(), [Validators.required]);
  orderStatusOptions: SelectInputOptionInterface[] = Object.entries(OrderStatusLabelMap).map(([value, label]) => ({
    label,
    value,
  })) as SelectInputOptionInterface[];

  originControl: UntypedFormControl = new UntypedFormControl(AnyOrigin, [Validators.required]);
  originOptions$: Observable<SelectInputOptionInterface[]>;
  descriptionKey = 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ORIGIN_DESCRIPTION';

  constructor(
    protected readonly automationService: AutomationViewService,
    protected readonly brandingService: BrandingService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.isWaitingForEvent) {
      this.descriptionKey = 'AUTOMATIONS.EDITOR.TRIGGERS.ORDER_STATUS_CHANGED.ACCOUNT_SCOPED_ORIGIN_DESCRIPTION';
    }

    if (this.triggerChoices?.rules?.length > 0) {
      const and = And.fromRuleInterface(this.triggerChoices.rules[0]);

      const orderStatusRule = and.value[0]?.value || Status.SUBMITTED.toString();
      const originRule = and.value[1]?.value || AnyOrigin;

      this.orderStatusControl.setValue(orderStatusRule.toString());
      this.originControl.setValue(originRule);
    } else {
      this.orderStatusControl.setValue(Status.SUBMITTED.toString());
      this.originControl.setValue(AnyOrigin);
    }

    this.orderStatusOptions = this.orderStatusOptions.map((option) => {
      if (option.value === this.orderStatusControl.value) {
        option.selected = true;
      }
      return option;
    });

    this.originOptions$ = this.automationService.isNonPartnerAutomation().pipe(
      take(1),
      switchMap((isNonPartnerAutomation) => {
        if (isNonPartnerAutomation) {
          return of('');
        }
        return this.brandingService.getBusinessAppName(this.automation.namespace, this.automation.marketId);
      }),
      switchMap((businessApp) => {
        const options = Object.entries(OrderOriginLabelMap).map(([value, label]) => {
          const optionEntry = { label, value };
          if (value === 'VBC') {
            optionEntry.label = businessApp || label;
          }
          return optionEntry;
        }) as SelectInputOptionInterface[];

        return of(options);
      }),
      map((originOptions) =>
        originOptions.map((option) => {
          if (option.value === this.originControl.value) {
            option.selected = true;
          }
          return option;
        }),
      ),

      // Once the origin options load, we can save.
      tap(() => this.savingEnabled$.next(true)),
    );
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.orderStatusControl, this.originControl];
  }

  isFormValid(): boolean {
    return this.orderStatusControl.valid && this.originControl.valid;
  }

  @TriggerBaseRules
  getBaseRules(): RuleInterface[] {
    const and = new And(
      [
        new NumberEquals('{.status}', this.orderStatusControl.value || Status.SUBMITTED, ''),
        new StringEquals('{.origin}', this.originControl.value || AnyOrigin),
      ],
      '',
    );

    return [and.toRuleInterface()];
  }

  fillRuleValues(rules: RuleInterface[]): RuleInterface[] {
    const and = And.fromRuleInterface(rules[0]);

    and.value[0].value = this.orderStatusControl.value;
    and.value[1].value = this.originControl.value;

    return [and.toRuleInterface()];
  }
}
